import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import "./Menu.css";
import { CartContext } from "./CartContext";
import vegLogo from "../img/veg.png";
import nonVegLogo from "../img/nonveg.png";
import { baseURL } from "./api";

const Menu = ({ restaurant, priceType: initialPriceType }) => {
  const { cart, addToCart, removeFromCart } = useContext(CartContext);
  const [menuItems, setMenuItems] = useState([]);
  const [updatedMenuItems, setUpdatedMenuItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [priceType, setPriceType] = useState(initialPriceType || "purchase");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedSubType, setSelectedSubType] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [packetSizes, setPacketSizes] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showToast, setShowToast] = useState(false);


  console.log("Restaurant in Menu.jsx:", restaurant);


  // ─────────────────────── Fetch Vendors ───────────────────────
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await fetch(`${baseURL}/getVendor`);
        if (!response.ok) throw new Error("Failed to fetch vendors");
        const data = await response.json();
        setVendors(data);

        const storedVendorId = localStorage.getItem("vendorId");
        const defaultVendor =
          data.find((v) => v._id === storedVendorId) || data[0];

        if (defaultVendor) {
          setSelectedVendor(defaultVendor);
          localStorage.setItem("vendorId", defaultVendor._id);
        }
      } catch (error) {
        setError(error.message);
      }
    };
    fetchVendors();
  }, []);

  // ─────────────────────── Fetch Menu Items ───────────────────────
  useEffect(() => {
    const fetchMenuItems = async () => {
      if (!restaurant) return;
      try {
        const response = await fetch(
          `${baseURL}/getMenuItems?restaurantId=${restaurant._id}`
        );
        if (!response.ok) throw new Error("Failed to fetch menu items");
        const data = await response.json();

        const activeItems = data.filter((item) => item.active === true);
        setMenuItems(activeItems);
        setUpdatedMenuItems(activeItems);

        // Initialize packet sizes
        const defaultPacketSizes = {};
        activeItems.forEach((item) => {
          if (item.customUnits && item.customUnits.length > 0) {
            defaultPacketSizes[item._id] = `${item.customUnits[0].qty}${item.customUnits[0].unit}`;
          } else {
            defaultPacketSizes[item._id] = "1kg";
          }

        });
        setPacketSizes(defaultPacketSizes);

        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMenuItems();
  }, [restaurant]);

  // ─────────────────────── Fetch Product Pricing ───────────────────────
  useEffect(() => {
    const fetchProductPricing = async () => {
      if (!selectedVendor || menuItems.length === 0) return;

      try {
        const response = await fetch(
          `${baseURL}/getProductPricing?vendorId=${selectedVendor._id}&restaurantId=${restaurant._id}`
        );
        if (!response.ok) throw new Error("Failed to fetch product pricing");

        const data = await response.json();

        const updatedItems = menuItems
          .map((item) => {
            const pricingForItem = data.pricing?.find(
              (p) => p.menuItemId?._id === item._id
            );

            let prices = {};

            /* 1️⃣ Product Pricing (highest priority) */
            if (pricingForItem?.units?.length > 0) {
              pricingForItem.units.forEach((u) => {
                const value =
                  priceType === "sale"
                    ? Number(u.salePrice)
                    : Number(u.purchasePrice);

                if (value > 0) {
                  prices[u.unit] = value;
                }
              });
            }

            /* 2️⃣ Menu → customUnits */
            if (Object.keys(prices).length === 0 && item.customUnits?.length > 0) {
              item.customUnits.forEach((cu) => {
                if (Number(cu.price) > 0) {
                  prices[`${cu.qty}${cu.unit}`] = Number(cu.price);
                }
              });
            }

            /* 3️⃣ Menu → weight based prices (🔥 MISSING BEFORE) */
            if (Object.keys(prices).length === 0) {
              if (Number(item.price1kg) > 0) {
                prices["1kg"] = Number(item.price1kg);
              }
              if (Number(item.price500gm) > 0) {
                prices["500gm"] = Number(item.price500gm);
              }
            }

            /* 4️⃣ Menu → base price */
            if (Object.keys(prices).length === 0 && Number(item.price) > 0) {
              prices["default"] = Number(item.price);
            }

            /* ❌ Hide ONLY if no price anywhere */
            if (Object.keys(prices).length === 0) return null;

            const firstKey = Object.keys(prices)[0];

            return {
              ...item,
              prices,
              price: prices[firstKey],
              priceSource: pricingForItem ? "pricing" : "menu",
            };
          })
          .filter(Boolean);

        setUpdatedMenuItems(updatedItems);

      } catch (error) {
        setError(error.message);
      }
    };
    fetchProductPricing();
  }, [selectedVendor, menuItems, restaurant._id, priceType]);

  // ─────────────────────── Filters ───────────────────────
  const availableTypes = ["All", ...new Set(menuItems.map((item) => item.type))];
  const availableSubTypesRaw = [
    ...new Set(
      menuItems
        .filter((item) => selectedType === "All" || item.type === selectedType)
        .map((item) => item.subType ?? "Others")
    ),
  ];
  const availableSubTypes = ["All", ...availableSubTypesRaw.filter((st) => st !== "Others"), "Others"];

  const filteredItems = updatedMenuItems.filter((item) => {
    const typeMatch = selectedType === "All" || item.type === selectedType;
    const subTypeMatch = selectedSubType === "All" || (item.subType ?? "Others") === selectedSubType;
    const searchMatch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return typeMatch && subTypeMatch && searchMatch;
  });

  const openProductModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const closeProductModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };


  // ─────────────────────── Quantity & Packet Handlers ───────────────────────
  const handleQuantityChange = (item, operation) => {
    const selectedPacket = packetSizes[item._id] || "1kg";
    const cartKey = `${item.id}-${selectedPacket}`;


    const cartItem = cart.find((c) => c.cartKey === cartKey);
    if (!cartItem) return;

    if (operation === "increment") {
      addToCart({ ...cartItem, quantity: cartItem.quantity + 1 });
    } else if (operation === "decrement") {
      if (cartItem.quantity > 1) {
        addToCart({ ...cartItem, quantity: cartItem.quantity - 1 });
      } else {
        removeFromCart(cartKey);
      }
    }
  };



  const handlePacketSizeChange = (itemId, value) => {
    setPacketSizes((prev) => ({ ...prev, [itemId]: value }));

    setUpdatedMenuItems((prev) =>
      prev.map((item) =>
        item._id === itemId
          ? { ...item, price: item.prices?.[value] ?? item.price ?? 0 }
          : item
      )
    );
  };

  const cartItem =
    selectedItem &&
    cart.find(
      (c) =>
        c._id === selectedItem._id &&
        c.packetSize === packetSizes[selectedItem._id]
    );

  useEffect(() => {
    if (showToast) {
      const t = setTimeout(() => setShowToast(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showToast]);

  const selectedPacketSize =
    packetSizes[selectedItem?._id] ||
    Object.keys(selectedItem?.prices || {})[0];

  const selectedPrice =
    selectedItem?.prices?.[selectedPacketSize] || 0;



  // ─────────────────────── Render ───────────────────────
  if (isLoading) return <div>Loading...</div>;

  return (
    <div>
      <input
        type="text"
        placeholder="Search menu item..."
        className="search-bar"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <section className="menu my-8">
        <div className="menu-head">
          <h2 className="Restitle">{restaurant.name} - Menu</h2>
          <button className="back" onClick={() => (window.location.href = "/home")}>
            Back to Home
          </button>
        </div>

        {/* Type Filter */}
        <div className="filter-options">
          {availableTypes.map((type) => (
            <button
              key={type}
              onClick={() => {
                setSelectedType(type);
                setSelectedSubType("All");
              }}
              className={`filter-btn ${selectedType === type ? "active" : ""}`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* SubType Filter */}
        {selectedType !== "All" && (
          <div className="filter-options">
            {availableSubTypes.map((subType) => (
              <button
                key={subType}
                onClick={() => setSelectedSubType(subType)}
                className={`filter-btn ${selectedSubType === subType ? "active" : ""}`}
              >
                {subType}
              </button>
            ))}
          </div>
        )}

        {filteredItems.length === 0 ? (
          <p>No menu items available for {selectedType} {selectedSubType !== "All" ? `- ${selectedSubType}` : ""}.</p>
        ) : (
          <div className="menu-grid">
            {filteredItems.map((item, index) => {
              const cartItem = cart.find(
                (c) =>
                  c._id === item._id &&
                  c.packetSize === (packetSizes[item._id] || "1kg")
              );



              return (
                <div
                  key={index}
                  className="menu-card"
                  onClick={() => openProductModal(item)}
                >

                  <img src={item.type === "Veg" ? vegLogo : nonVegLogo} alt={item.type} className="food-logo" />
                  <img src={`${baseURL}${item.imgPath}`} alt={item.name} className="menu-img" />
                  <div className="menu-info">
                    <h3 className="menu-name">{item.name}</h3>
                    <p className="menu-desc">{item.description}</p>
                    <span
                      className="read-more"
                      onClick={() => openProductModal(item)}
                    >
                      Read more
                    </span>


                    <div
                      style={{ display: "flex", alignItems: "center", gap: "10px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.customUnits && item.customUnits.length > 0 ? (
                        // 🔥 CUSTOM UNITS DROPDOWN
                        <select
                          value={packetSizes[item._id]}
                          onChange={(e) => handlePacketSizeChange(item._id, e.target.value)}
                          className="packet-select"
                        >
                          {item.customUnits.map((cu) => (
                            <option
                              key={`${cu.qty}${cu.unit}`}
                              value={`${cu.qty}${cu.unit}`}
                            >
                              {cu.qty} {cu.unit}
                            </option>
                          ))}
                        </select>
                      ) : (
                        // 🔥 NORMAL PACKET DROPDOWN
                        <select
                          value={packetSizes[item._id]}
                          onChange={(e) => handlePacketSizeChange(item._id, e.target.value)}
                          className="packet-select"
                        >
                          {Object.keys(item.prices || {}).map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>

                      )}

                      <span className="menu-price">₹ {item.price}</span>
                    </div>



                    {cartItem ? (
                      <div className="qty-row">
                        {/* Packet size dropdown */}

                        {/* Quantity controls */}
                        <div className="item-quantity">
                          <button
                            className="filter-btn"
                            onClick={(e) => { e.stopPropagation(); handleQuantityChange(cartItem, "decrement") }}
                          >
                            −
                          </button>
                          <span>{cartItem.quantity}</span>
                          <button
                            className="filter-btn"
                            onClick={(e) => { e.stopPropagation(); handleQuantityChange(cartItem, "increment") }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart({
                            ...item,
                            quantity: 1,
                            addOns: [],
                            packetSize:
                              packetSizes[item._id] ||
                              Object.keys(item.prices || {})[0],

                            price:
                              item.prices?.[packetSizes[item._id] || "1kg"] ??
                              item.price ??
                              0,
                          })
                        }}
                      >
                        Add to Cart
                      </button>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      {showModal && selectedItem && (

        <div className="product-modal-overlay" onClick={closeProductModal}>
          <div
            className="product-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >


            {/* LEFT IMAGE SECTION */}
            <div className="modal-left">
              <div className="thumbnail-list">
                <img
                  src={`${baseURL}${selectedItem.imgPath}`}
                  alt="thumb"
                  className="thumbnail active"
                />
                {/* future thumbnails */}
              </div>

              <div className="main-image">
                <img
                  src={`${baseURL}${selectedItem.imgPath}`}
                  alt={selectedItem.name}
                />
              </div>
            </div>

            {/* RIGHT DETAILS SECTION */}
            <div className="modal-right">
              <button className="modal-close" onClick={closeProductModal}>
                ✕
              </button>

              <p className="modal-short-desc">
                {selectedItem.shortDescription || selectedItem.description}
              </p>

              <ul className="feature-list">
                <li>✔ Bite-sized</li>
                <li>✔ Vacuum packed</li>
                <li>✔ Cold-Cut</li>
              </ul>

              {/* Packet size */}
              <select
                value={packetSizes[selectedItem._id]}
                onChange={(e) =>
                  handlePacketSizeChange(selectedItem._id, e.target.value)
                }
                className="packet-select"
              >
                {Object.keys(selectedItem.prices || {}).map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
              <p className="modal-price">
                Price: <strong>₹ {selectedPrice}</strong>
              </p>

              {cartItem ? (
                <div className="qty-row">
                  <div className="item-quantity">
                    <button
                      className="filter-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(cartItem, "decrement");
                      }}
                    >
                      −
                    </button>

                    <span>{cartItem.quantity}</span>



                    <button
                      className="filter-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(cartItem, "increment");
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="add-to-cart-btn"
                  onClick={(e) => {
                    e.stopPropagation();

                    addToCart({
                      ...selectedItem,
                      quantity: 1,
                      addOns: [],
                      packetSize:
                        packetSizes[selectedItem._id] ||
                        Object.keys(selectedItem.prices || {})[0],
                      cartKey: `${selectedItem._id}-${packetSizes[selectedItem._id] ||
                        Object.keys(selectedItem.prices || {})[0]
                        }`,
                      price:
                        selectedItem.prices?.[
                        packetSizes[selectedItem._id] ||
                        Object.keys(selectedItem.prices || {})[0]
                        ] || 0,
                    });

                    setShowToast(true);
                  }}
                >
                  Add to Cart
                </button>

              )}
              <p className="category-text">
                Category: <strong>{selectedItem.subType || "Cold Cuts"}</strong>
              </p>
            </div>
          </div>
        </div>
      )}
      {showToast && (
        <div className="cart-toast">
          ✅ Item added to cart
        </div>
      )}

    </div>
  );
};

export default Menu;
